import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
    TextField
} from "@mui/material";

import { useState, useEffect } from "react";
import { useTarefa } from "../../../../../contexts/TarefaContext";
import { ITarefaBancoDados, TipoBackupBancoDados } from "../../../../../types/tarefa";
import { v4 as uuidv4 } from 'uuid';
import BFTextFieldPassword from "../../../../BFTextFieldPassword";
import {BFGridOneLine, BFGridContainer } from "../../../../BFGrid";
import { BFChange } from "../../../../../types/components";
import { useLogin } from "../../../../../contexts/LoginContext";
import { useCliente } from "../../../../../contexts/ClienteContext";
import { TarefasApi } from "../../../services/tarefasApi";

const margin = { marginTop: '24px' };

const BFTextField = styled(TextField)({
    ...margin
});

const BackupBancoDados = () => {
    const [tarefa, setTarefa] = useTarefa();
    const [login] = useLogin();
    const [cliente] = useCliente();

    const configuracaoInicial: ITarefaBancoDados = tarefa.configBancoDados ? tarefa.configBancoDados : {
        id: uuidv4(),
        tipo: "",
        servidor: "",
        bancoDados: "",
        porta: 1433,
        usuario: "sa",
        senha: "",
        descricao: ""
    };

    const [configBancodados, setConfigBancodados] = useState<ITarefaBancoDados>({ ...configuracaoInicial });
    const [sugestoes, setSugestoes] = useState<any[]>([]);
    const [loadingSugestoes, setLoadingSugestoes] = useState(false);
    const [selecaoSugestaoID, setSelecaoSugestaoID] = useState<string>(configuracaoInicial.sugestaoID || "");

    useEffect(() => {
        const clienteID = cliente.id || login.usuario?.usuarioCliente?.clienteID;
        if (clienteID && login.accessToken) {
            setLoadingSugestoes(true);
            TarefasApi.obterSugestoesBancoDados(String(clienteID), login.accessToken)
                .then(data => {
                    setSugestoes(data || []);
                })
                .catch(err => {
                    console.error("Erro ao obter sugestões de banco de dados:", err);
                })
                .finally(() => {
                    setLoadingSugestoes(false);
                });
        }
    }, [cliente.id, login.usuario?.usuarioCliente?.clienteID, login.accessToken]);

    function handleSelecionarSugestao(event: SelectChangeEvent<string>): void {
        const value = event.target.value;
        setSelecaoSugestaoID(value);
        if (value) {
            const sug = sugestoes.find(s => s.id === value);
            if (sug) {
                const novaConfig = {
                    ...configBancodados,
                    servidor: sug.servidor || "",
                    bancoDados: sug.bancoDados || "",
                    porta: sug.porta || 1433,
                    usuario: sug.usuario || "",
                    senha: sug.senha || "",
                    descricao: sug.descricao || "",
                    sugestaoID: sug.id
                };
                mudarEstadoTarefa(novaConfig);
            }
        } else {
            // Reverte/limpa sugestão
            const novaConfig = {
                ...configBancodados,
                sugestaoID: undefined
            };
            mudarEstadoTarefa(novaConfig);
        }
    }

    function handleTipo(event: SelectChangeEvent<TipoBackupBancoDados>): void {
        mudarEstadoTarefa({ ...configBancodados, tipo: event.target.value as TipoBackupBancoDados });
    }

    function handleServidor(event: BFChange): void {
        mudarEstadoTarefa({ ...configBancodados, servidor: event.target.value, sugestaoID: undefined });
        setSelecaoSugestaoID("");
    }

    function handleBancoDados(event: BFChange): void {
        mudarEstadoTarefa({ ...configBancodados, bancoDados: event.target.value, sugestaoID: undefined });
        setSelecaoSugestaoID("");
    }

    function handlePorta(event: BFChange): void {
        mudarEstadoTarefa({ ...configBancodados, porta: Number(event.target.value), sugestaoID: undefined });
        setSelecaoSugestaoID("");
    }

    function handleUsuario(event: BFChange): void {
        mudarEstadoTarefa({ ...configBancodados, usuario: event.target.value, sugestaoID: undefined });
        setSelecaoSugestaoID("");
    }

    function handleSenha(event: BFChange): void {
        mudarEstadoTarefa({ ...configBancodados, senha: event.target.value, sugestaoID: undefined });
        setSelecaoSugestaoID("");
    }

    function handleDescricao(event: BFChange): void {
        mudarEstadoTarefa({ ...configBancodados, descricao: event.target.value, sugestaoID: undefined });
        setSelecaoSugestaoID("");
    }

    function mudarEstadoTarefa(novaConfiguracao: ITarefaBancoDados): void {
        setConfigBancodados(novaConfiguracao);
        setTarefa({ ...tarefa, configBancoDados: novaConfiguracao });
    }

    return (
        <BFGridContainer>
            <BFGridOneLine>
                <FormControl fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="conexao-sugerida-label">Sugestão de Conexão Existente</InputLabel>
                    <Select
                        labelId="conexao-sugerida-label"
                        id="conexao-sugerida"
                        value={selecaoSugestaoID}
                        label="Sugestão de Conexão Existente"
                        onChange={handleSelecionarSugestao}
                        disabled={loadingSugestoes}
                    >
                        <MenuItem value="">
                            <em>-- Configuração Nova / Personalizada --</em>
                        </MenuItem>
                        {sugestoes.map((sug) => (
                            <MenuItem key={sug.id} value={sug.id}>
                                {sug.descricao} ({sug.servidor} &rarr; {sug.bancoDados})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </BFGridOneLine>
            <BFGridOneLine>
                <FormControl fullWidth>
                    <InputLabel id="tipo-label">{tarefa.tipo === 'CheckDB' ? 'Banco de Dados (SGBD)' : 'Tipo'}</InputLabel>
                    <Select
                        labelId="tipo-label"
                        id="tipo"
                        value={configBancodados.tipo}
                        label={tarefa.tipo === 'CheckDB' ? 'Banco de Dados (SGBD)' : 'Tipo'}
                        required
                        onChange={handleTipo}
                    >
                        {tarefa.tipo === 'CheckDB' ? [
                            <MenuItem key="SQLServer" value="SQLServer">SQL Server</MenuItem>,
                            <MenuItem key="Postgres" value="Postgres">PostgreSQL</MenuItem>,
                            <MenuItem key="Sybase" value="Sybase">SAP SQL Anywhere (Sybase)</MenuItem>
                        ] : [
                            <MenuItem key="Completo" value="Completo">Completo</MenuItem>,
                            <MenuItem key="Diferencial" value="Diferencial">Diferencial</MenuItem>,
                            <MenuItem key="Log" value="Log">Log de Transação</MenuItem>
                        ]}
                    </Select>
                </FormControl>
            </BFGridOneLine>
            <BFGridOneLine>
                <BFTextField
                    fullWidth
                    id="descricao"
                    name="descricao"
                    label="Descrição"
                    variant="outlined"
                    required
                    value={configBancodados.descricao}
                    onChange={handleDescricao} />
            </BFGridOneLine>            
            <BFGridOneLine>
                <BFTextField
                    fullWidth
                    id="servidor"
                    name="servidor"
                    label="Servidor / Instância"
                    variant="outlined"
                    required
                    value={configBancodados.servidor}
                    onChange={handleServidor} />
            </BFGridOneLine>
            <BFGridOneLine>
                <BFTextField
                    fullWidth
                    id="bancoDados"
                    name="bancoDados"
                    label="Banco de Dados"
                    variant="outlined"
                    required
                    value={configBancodados.bancoDados}
                    onChange={handleBancoDados} />
            </BFGridOneLine>
            <BFGridOneLine>
                <BFTextField
                    fullWidth
                    id="porta"
                    type="number"
                    name="porta"
                    label="Porta"
                    variant="outlined"
                    required
                    value={configBancodados.porta}
                    onChange={handlePorta} />
            </BFGridOneLine>
            <BFGridOneLine>
                <BFTextField
                    fullWidth
                    id="usuario"
                    name="usuario"
                    label="Usuário"
                    variant="outlined"
                    required
                    value={configBancodados.usuario}
                    onChange={handleUsuario} />
            </BFGridOneLine>
            <BFGridOneLine>
                <BFTextFieldPassword
                    fullWidth
                    sx={{ ...margin }}
                    id="senha"
                    type="password"
                    name="senha"
                    label="Senha"
                    variant="outlined"
                    required
                    value={configBancodados.senha}
                    onChange={handleSenha} />
            </BFGridOneLine>
        </BFGridContainer>
    );
}

export default BackupBancoDados;